import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowDownUp, ArrowRight, ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCryptoIconFallback } from "@/lib/cryptoIcons";
import { formatAssetAmount, getAssetMaxDecimals, toAssetInputString } from "@/lib/amountFormat";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";

const SwapPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, balances, refreshData, haptic, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [fromCurrency, setFromCurrency] = useState('USDT');
  const [toCurrency, setToCurrency] = useState('TON');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [quoteRate, setQuoteRate] = useState(null);
  const [quoteFeeAmount, setQuoteFeeAmount] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [swapStatus, setSwapStatus] = useState(null);
  const [swapId, setSwapId] = useState(null);
  const [pendingSwaps, setPendingSwaps] = useState([]);
  const [localSwap, setLocalSwap] = useState(null);
  const prefillAppliedRef = useRef(false);

  const fromBalance = (() => {
    const row = balances.find((b) => b.currency === fromCurrency);
    return Number(row?.available ?? row?.amount ?? 0);
  })();
  const fromBalanceExact = (() => {
    if (!Number.isFinite(fromBalance)) return "0";
    return toAssetInputString(fromBalance, fromCurrency);
  })();
  const getCurrencyIcon = useCallback((currency) => {
    const code = String(currency || "").toUpperCase();
    const row = balances.find((b) => String(b?.currency || "").toUpperCase() === code);
    return row?.icon || "";
  }, [balances]);
  const fromIcon = getCurrencyIcon(fromCurrency);
  const toIcon = getCurrencyIcon(toCurrency);
  const normalizedStatus = (status) => String(status || "").toLowerCase();
  const inProgressStatuses = new Set([
    'queued',
    'init',
    'processing',
    'timeout',
    'awaiting_approval',
    'waiting',
    'created'
  ]);
  const isSwapping = inProgressStatuses.has(normalizedStatus(swapStatus));
  const statusLabel = (status) => {
    const st = normalizedStatus(status);
    if (!st) return "";
    const labels = {
      queued: t('swap.status_queued', 'В очереди'),
      init: t('swap.status_init', 'Подготовка'),
      processing: t('swap.status_processing', 'В обработке'),
      waiting: t('swap.status_waiting', 'Ожидание'),
      awaiting_approval: t('swap.status_awaiting_approval', 'Требует подтверждения'),
      timeout: t('swap.status_timeout', 'Проверяем дольше обычного'),
      finished: t('swap.status_finished', 'Завершено'),
      failed: t('swap.status_failed', 'Ошибка')
    };
    return labels[st] || st;
  };
  const mapSwapError = useCallback((rawError) => {
    const raw = String(rawError || "").trim();
    if (!raw) return t("swap.failed");
    const normalized = raw.toLowerCase();
    if (normalized.includes("insufficient balance") || normalized.includes("insufficient_balance")) {
      return t('common.insufficient_balance_error');
    }
    return t("swap.failed");
  }, [t]);
  const displaySwaps = (() => {
    const list = Array.isArray(pendingSwaps) ? [...pendingSwaps] : [];
    if (localSwap) {
      const exists = list.some((s) => s.swap_id === localSwap.swap_id);
      if (!exists) list.unshift(localSwap);
    }
    return list;
  })();
  const amountNum = parseFloat(fromAmount);
  const hasValidAmount = !Number.isNaN(amountNum) && amountNum > 0;
  const roundDownFixed = useCallback((value, decimals = 6) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const d = Math.max(0, Number(decimals) || 0);
    const factor = 10 ** d;
    const floored = Math.floor((n + Number.EPSILON) * factor) / factor;
    return floored.toFixed(d);
  }, []);

  useEffect(() => {
    if (prefillAppliedRef.current) return;
    if (!Array.isArray(balances) || balances.length === 0) return;

    const knownCurrencies = balances
      .map((b) => String(b?.currency || "").toUpperCase())
      .filter(Boolean);
    const requestedTo = String(searchParams.get("to") || "").toUpperCase();
    const requestedFrom = String(searchParams.get("from") || "").toUpperCase();

    const validTo = knownCurrencies.includes(requestedTo) ? requestedTo : "";
    const bestFromCoin =
      balances
        .map((b) => ({
          currency: String(b?.currency || "").toUpperCase(),
          available: Number(b?.available ?? b?.amount ?? 0),
        }))
        .filter((row) => row.currency && row.currency !== validTo && row.available > 0)
        .sort((a, b) => b.available - a.available)[0]?.currency || "USDT";

    let nextFrom = knownCurrencies.includes(requestedFrom) ? requestedFrom : "";
    if (!nextFrom || nextFrom === validTo) {
      nextFrom = knownCurrencies.includes(bestFromCoin) ? bestFromCoin : (knownCurrencies[0] || fromCurrency);
    }
    const nextTo = validTo || toCurrency;

    if (nextTo && nextTo !== toCurrency) {
      setToCurrency(nextTo);
    }
    if (nextFrom && nextFrom !== nextTo && nextFrom !== fromCurrency) {
      setFromCurrency(nextFrom);
    } else if (nextFrom === nextTo) {
      const fallback = knownCurrencies.find((cur) => cur !== nextTo) || "USDT";
      if (fallback !== fromCurrency) setFromCurrency(fallback);
    }

    prefillAppliedRef.current = true;
  }, [balances, searchParams, fromCurrency, toCurrency]);

  useEffect(() => {
    if (fromCurrency === toCurrency) {
      if (hasValidAmount) {
      setToAmount(toAssetInputString(amountNum, toCurrency || undefined));
      } else {
        setToAmount('');
      }
      setQuoteRate(1);
      setQuoteFeeAmount(0);
      return;
    }

    const quoteAmount = hasValidAmount ? amountNum : 1;
    const timer = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const res = await api.post('/swap/quote', {
          from_currency: fromCurrency,
          to_currency: toCurrency,
          amount: quoteAmount
        });
        const toAmt = parseFloat(res.data?.to_amount || 0);
        const feeAmount = parseFloat(res.data?.fee_amount || 0);
        const rateFromMath = (quoteAmount > 0 && Number.isFinite(toAmt) && Number.isFinite(feeAmount))
          ? (toAmt + feeAmount) / quoteAmount
          : NaN;
        const rateFromPre = Number(res.data?.rate_pre_rapira);
        const rateFromLegacy = Number(res.data?.rate);
        const rate = [rateFromPre, rateFromMath, rateFromLegacy].find((v) => Number.isFinite(v) && v > 0) ?? 0;

        setToAmount(hasValidAmount && toAmt > 0 ? toAssetInputString(toAmt, toCurrency || undefined) : '');
        setQuoteRate(rate > 0 ? rate : null);
        setQuoteFeeAmount(Number.isFinite(feeAmount) ? feeAmount : null);
      } catch (err) {
        setToAmount('');
        setQuoteRate(null);
        setQuoteFeeAmount(null);
      } finally {
        setQuoteLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [api, fromAmount, amountNum, hasValidAmount, fromCurrency, toCurrency]);

  const handleSwapCurrencies = () => {
    haptic('light');
    const temp = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(temp);
    setFromAmount('');
    setToAmount('');
    setQuoteRate(null);
    setQuoteFeeAmount(null);
  };

  const handleSwap = async () => {
    haptic('medium');

    const amount = parseFloat(fromAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('common.valid_amount_error'));
      return;
    }

    if (amount > fromBalance) {
      toast.error(t('common.insufficient_balance_error'));
      return;
    }
    setLoading(true);
    setSwapStatus('processing');
    setSwapId(null);
    setLocalSwap(null);
    try {
      const res = await api.post('/swap', {
        from_currency: fromCurrency,
        to_currency: toCurrency,
        amount
      });

      const nextStatus = res.data?.status || 'processing';
      const nextSwapId = res.data?.swap_id || null;
      setSwapStatus(nextStatus);
      setSwapId(nextSwapId);
      setLocalSwap({
        swap_id: nextSwapId || `local:${Date.now()}`,
        from_currency: fromCurrency,
        to_currency: toCurrency,
        from_amount: amount,
        to_amount: res.data?.to_amount ?? null,
        fee_amount: Number(res.data?.fee_amount || 0),
        status: nextStatus,
        provider_id: null,
      });
      if (res.data?.status === 'finished') {
        toast.success(t('swap.success', { fromAmount: amount, fromCurrency, toAmount: res.data.to_amount, toCurrency }));
        await refreshData();
        setSwapStatus(null);
        setSwapId(null);
        setLocalSwap(null);
        navigate('/');
      } else {
        toast.message(t('swap.processing', 'Обмен в обработке...'));
      }
    } catch (err) {
      toast.error(mapSwapError(err.response?.data?.detail));
      setSwapStatus(null);
      setSwapId(null);
      setLocalSwap(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMax = () => {
    haptic('light');
    setFromAmount(fromBalanceExact);
  };

  const handleBack = useCallback(() => {
    haptic?.("light");
    navigate(-1);
  }, [haptic, navigate]);

  useEffect(() => {
    if (!swapId || !isSwapping) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/swap/status/${swapId}`);
        const status = normalizedStatus(res.data?.swap?.status);
        const toAmountFinal = res.data?.swap?.to_amount;
        const swapError = res.data?.swap?.error;
        if (status === 'finished') {
          if (!cancelled) {
            toast.success(t('swap.success', { fromAmount: fromAmount || 0, fromCurrency, toAmount: toAmountFinal || toAmount, toCurrency }));
            setSwapStatus('finished');
            setLocalSwap((prev) => prev ? { ...prev, status: 'finished', to_amount: toAmountFinal ?? prev.to_amount } : prev);
            await refreshData();
            setSwapStatus(null);
            setSwapId(null);
            setLocalSwap(null);
            navigate('/');
          }
          return;
        }
        if (status === 'failed') {
          if (!cancelled) {
            toast.error(mapSwapError(swapError));
            setSwapStatus(null);
            setSwapId(null);
            setLocalSwap((prev) => prev ? { ...prev, status: 'failed', error: swapError } : prev);
          }
          return;
        }
        if (!cancelled && status) {
          setSwapStatus(status);
          setLocalSwap((prev) => prev ? { ...prev, status, to_amount: toAmountFinal ?? prev.to_amount } : prev);
        }
        if (!cancelled) setTimeout(poll, 1500);
      } catch {
        if (!cancelled) setTimeout(poll, 2000);
      }
    };
    const timer = setTimeout(poll, 1200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, swapId, isSwapping, refreshData, navigate, t, fromAmount, fromCurrency, toCurrency, toAmount, mapSwapError]);

  useEffect(() => {
    let cancelled = false;
    const loadPending = async () => {
      try {
        const res = await api.get('/swap/recent');
        const list = res.data?.swaps || [];
        if (!cancelled) setPendingSwaps(list);
      } catch {
        if (!cancelled) setPendingSwaps([]);
      }
    };
    loadPending();
    const timer = setInterval(loadPending, 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  return (
    <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col animate-fade-in" data-testid="swap-page">
      <div className="lp-bar lp-topbar sticky top-0 z-30 px-4 py-3 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
        {showInlineBack ? (
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-8 w-8 items-center justify-center text-foreground"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : (
          <span aria-hidden="true" className="h-8 w-8" />
        )}
        <h1 className="text-center text-[17px] font-semibold text-foreground">{t("actions.swap")}</h1>
        <span aria-hidden="true" className="h-8 w-8" />
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-6 pb-[calc(7rem+var(--lp-bottom))] space-y-6">
        {/* Swap Form */}
        <div className="space-y-2">
        {/* From */}
        <div className="lp-glass p-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('swap.from')}</span>
            <button
              onClick={handleMax}
              className="text-link font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="from-max-btn"
              disabled={isSwapping}
            >
              {t('common.max')}: {fromBalanceExact}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              value={fromAmount}
              onChange={(e) => setFromAmount(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="0.00"
              className="border-none text-2xl font-mono h-12 p-0 focus-visible:ring-0"
              data-testid="from-amount-input"
              disabled={isSwapping}
            />
            <Select value={fromCurrency} onValueChange={setFromCurrency} disabled={isSwapping}>
              <SelectTrigger className="w-44 sm:w-48" data-testid="from-currency-select">
                <div className="flex items-center gap-2">
                  <img
                    src={fromIcon || getCryptoIconFallback(fromCurrency)}
                    alt={fromCurrency}
                    className="w-5 h-5 rounded-full"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = getCryptoIconFallback(fromCurrency);
                    }}
                  />
                  <span>{fromCurrency}</span>
                </div>
              </SelectTrigger>
              <SelectContent className="min-w-[7.5rem]">
                {balances.map((b) => (
                  <SelectItem key={b.currency} value={b.currency}>
                    <div className="flex items-center gap-2">
                      <img
                        src={b.icon}
                        alt={b.currency}
                        className="w-5 h-5 rounded-full"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = getCryptoIconFallback(b.currency);
                        }}
                      />
                      <span>{b.currency}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Swap Button */}
        <div className="flex justify-center -my-3 relative z-10">
          <button
            onClick={handleSwapCurrencies}
            className="w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="swap-direction-btn"
            disabled={isSwapping}
          >
            <ArrowDownUp className="w-5 h-5" />
          </button>
        </div>

        {/* To */}
        <div className="lp-glass p-4 space-y-3">
          <span className="text-sm text-muted-foreground">{t('swap.to_estimated')}</span>
          <div className="flex items-center gap-3">
            <Input
              type="text"
              value={toAmount}
              readOnly
              placeholder="0.00"
              className="border-none text-2xl font-mono h-12 p-0 focus-visible:ring-0 bg-transparent"
              data-testid="to-amount-input"
            />
            <Select value={toCurrency} onValueChange={setToCurrency} disabled={isSwapping}>
              <SelectTrigger className="w-44 sm:w-48" data-testid="to-currency-select">
                <div className="flex items-center gap-2">
                  <img
                    src={toIcon || getCryptoIconFallback(toCurrency)}
                    alt={toCurrency}
                    className="w-5 h-5 rounded-full"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = getCryptoIconFallback(toCurrency);
                    }}
                  />
                  <span>{toCurrency}</span>
                </div>
              </SelectTrigger>
              <SelectContent className="min-w-[7.5rem]">
                {balances.map((b) => (
                  <SelectItem
                    key={b.currency}
                    value={b.currency}
                    disabled={b.currency === fromCurrency}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src={b.icon}
                        alt={b.currency}
                        className="w-5 h-5 rounded-full"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = getCryptoIconFallback(b.currency);
                        }}
                      />
                      <span>{b.currency}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        </div>

        {/* Rate Info */}
        <div className="bg-[var(--lp-tile)] rounded-xl p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{t('common.exchange_rate')}</span>
          <span className="font-mono">
            {quoteLoading ? '...' : `1 ${fromCurrency} ≈ ${quoteRate ? (roundDownFixed(quoteRate, getAssetMaxDecimals(toCurrency || 'USDT')) || '—') : '—'} ${toCurrency}`}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">
            {t('swap.fee_in_to', 'Fee in {{currency}}', { currency: toCurrency })}
          </span>
          <span className="font-mono">
            {quoteLoading
              ? '...'
              : hasValidAmount && quoteFeeAmount !== null
                ? `${formatAssetAmount(quoteFeeAmount, toCurrency, { minDecimals: 0, maxDecimals: 8 })} ${toCurrency}`
                : '—'}
          </span>
        </div>
        </div>

        {/* Swap Button */}
        <Button
        onClick={handleSwap}
        disabled={loading || isSwapping || !hasValidAmount || parseFloat(fromAmount) > fromBalance}
        className="w-full h-14 rounded-full text-lg mb-6"
        data-testid="swap-btn"
        >
        {loading || isSwapping ? (
          <div className="spinner mr-2" />
        ) : (
          <ArrowRight className="w-5 h-5 mr-2" />
        )}
        {t('swap.title')}
        </Button>
        {isSwapping && (
        <p className="text-center text-sm text-muted-foreground">
          {statusLabel(swapStatus) || t('swap.processing', 'Обмен в обработке...')}
        </p>
        )}
        {displaySwaps.length > 0 && (
        <div className="lp-glass p-4 space-y-2">
          <div className="text-sm font-medium">{t('swap.pending', 'Текущие обмены')}</div>
          {displaySwaps.map((swap) => (
            <div key={swap.swap_id} className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="font-mono">
                {swap.from_amount} {swap.from_currency} → {swap.to_currency}
              </span>
              <span>
                {normalizedStatus(swap.status) === 'failed' && swap.error
                  ? mapSwapError(swap.error)
                  : (statusLabel(swap.status) || swap.status)}
              </span>
            </div>
          ))}
        </div>
        )}
        {displaySwaps.length === 0 && isSwapping && (
        <div className="lp-glass p-4 text-xs text-muted-foreground text-center">
          {statusLabel(swapStatus) || t('swap.processing', 'Обмен в обработке...')}
        </div>
        )}
      </div>
    </div>
  );
};

export default SwapPage;
